import { ButtonLoading } from "@/components/button-loading";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { useToastError } from "@/components/ui/use-toast";
import {
  CREDITS_PACKAGES,
  CREDITS_RETRIEVAL_COST,
  getPackageWithPromo,
} from "@/contexts/credits-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { ArrowRightIcon, BadgeCentIcon, TagIcon } from "lucide-react";
import { useState } from "react";

interface DialogPurchaseCreditsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentCredits?: number;
  onCreditsPurchased?: () => void;
}

export function DialogPurchaseCredits({
  open,
  onOpenChange,
  currentCredits = 0,
}: DialogPurchaseCreditsProps) {
  const { t } = useTranslation();
  const api = useApi();
  const toastError = useToastError();

  const [selectedPackageIndex, setSelectedPackageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const selectedPackage = getPackageWithPromo(
    CREDITS_PACKAGES[selectedPackageIndex]
  );

  const handlePurchase = async () => {
    setIsLoading(true);

    try {
      const credits = Number(selectedPackage?.credits);

      if (!selectedPackage || !credits || isNaN(credits) || credits <= 0) {
        throw new Error("Invalid credits amount");
      }

      const { data } = await api.post<{ checkoutUrl: string }>(
        "/my/team/credits/purchase",
        {
          credits,
        }
      );

      // Store credits amount in sessionStorage for toast display after redirect
      if (data.checkoutUrl) {
        sessionStorage.setItem("stripe-checkout-credits", credits.toString());

        // Redirect to Stripe Checkout
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      setIsLoading(false);
      toastError();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-2">
              <div className="flex justify-center items-center w-8 h-8 bg-gray-100 rounded-full">
                <BadgeCentIcon className="w-4 h-4 text-gray-600" />
              </div>
              <span className="font-semibold text-gray-900">
                {t("team.credits.purchase.title")}
              </span>
            </div>
          </div>
        </DialogHeader>

        <div className="mb-6 text-center">
          <div className="flex gap-3 justify-center items-center mb-4">
            <h2 className="text-3xl font-bold text-gray-900">
              {selectedPackage.credits.toLocaleString()}{" "}
              {t("team.credits.purchase.credits")}
            </h2>
            {selectedPackage.hasValidPromo && (
              <div className="flex gap-1 items-center px-3 py-1 text-sm font-semibold text-green-800 bg-green-100 rounded-full">
                <TagIcon className="w-3 h-3" />-
                {selectedPackage.discountPercentage}%
              </div>
            )}
          </div>

          <div className="mb-6">
            <div className="flex justify-between mb-2">
              {CREDITS_PACKAGES.map((pkg, index) => (
                <div key={pkg.credits} className="flex flex-col items-center">
                  <span
                    className={`text-sm font-medium transition-colors ${
                      selectedPackageIndex === index
                        ? "font-bold"
                        : "text-gray-400"
                    }`}
                  >
                    {pkg.label}
                  </span>
                  <div className="w-px h-2 bg-gray-300"></div>
                </div>
              ))}
            </div>
            <Slider
              value={[selectedPackageIndex]}
              onValueChange={(value) => setSelectedPackageIndex(value[0])}
              max={CREDITS_PACKAGES.length - 1}
              min={0}
              step={1}
              className="w-full transition-colors duration-100 cursor-pointer hover:bg-gray-300"
            />
          </div>

          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">
              {t("team.credits.purchase.phonePreview", {
                cost: CREDITS_RETRIEVAL_COST.PHONE,
                count: Math.floor(
                  selectedPackage.credits / CREDITS_RETRIEVAL_COST.PHONE
                ),
              })}
            </p>
          </div>
        </div>

        <div className="flex flex-col pt-4 mb-4 space-y-4 border-t">
          {selectedPackage.hasValidPromo && (
            <Alert variant="default" className="bg-green-50 border-green-200">
              <TagIcon className="text-green-600" />
              <AlertTitle className="text-green-800">
                🎉 {t("team.credits.purchase.promo.limitedOffer")}
              </AlertTitle>
              <AlertDescription className="text-green-700">
                {t("team.credits.purchase.promo.description", {
                  validUntil: t("team.credits.purchase.promo.validUntil", {
                    date: selectedPackage.promoDateEnd?.toLocaleDateString(
                      "fr-FR",
                      {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      }
                    ),
                  }),
                  enjoy: t("team.credits.purchase.promo.enjoy"),
                  discount: t("team.credits.purchase.promo.discount", {
                    percentage: selectedPackage.discountPercentage,
                  }),
                  allPackages: t("team.credits.purchase.promo.allPackages"),
                })}
              </AlertDescription>
            </Alert>
          )}

          <Alert variant="warning">
            <BadgeCentIcon />
            <AlertDescription className="text-left">
              {t("team.credits.purchase.infoBanner", {
                count: selectedPackage.credits,
                current: currentCredits,
              })}
            </AlertDescription>
          </Alert>

          <div className="flex gap-4 justify-end items-center">
            <div className="text-right">
              {selectedPackage.hasValidPromo ? (
                <div className="space-y-1">
                  <div className="flex gap-2 justify-end items-center">
                    <span className="text-lg text-gray-500 line-through">
                      {t("team.credits.purchase.price", {
                        amount: selectedPackage.originalPrice,
                      })}
                    </span>
                    <div className="text-2xl font-bold text-gray-900">
                      {t("team.credits.purchase.price", {
                        amount: selectedPackage.finalPrice,
                      })}
                    </div>
                  </div>
                  <div className="text-sm font-medium text-green-600">
                    {t("team.credits.purchase.promo.saveAmount", {
                      amount: t("team.credits.purchase.price", {
                        amount: selectedPackage.savings,
                      }),
                    })}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t("team.credits.purchase.oneTimePayment")}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-2xl font-bold">
                    {t("team.credits.purchase.price", {
                      amount: selectedPackage.finalPrice,
                    })}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t("team.credits.purchase.oneTimePayment")}
                  </div>
                </div>
              )}
            </div>
            <ButtonLoading
              onClick={handlePurchase}
              disabled={isLoading}
              className="py-3 font-medium rounded-lg grow-0"
              isLoading={isLoading}
            >
              <ArrowRightIcon className="mr-2 w-4 h-4" />
              {t("team.credits.purchase.confirmPurchase")}
            </ButtonLoading>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
